import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { IUserData } from '@interfaces';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/auth';
import { UtilValidatorsService } from 'src/app/commons/validations-format/util-validators.service';
import { UtilService } from 'src/app/services/util.service';
import { IHeaderData } from 'src/app/shared/header-data/header-data.models';
import { SetNewUser } from '../../models/user-management-model';
import { UserManagementService } from '../../services/user-management.service';

enum USER_ROLE {
  comercial = 1,
  gestor,
  backoffice
}
enum PERMISSION {
  "only read" = 1,
  "create user",
  "settlement view"
}

@Component({
  selector: 'app-new-user',
  templateUrl: './new-user.component.html',
  styleUrls: ['./new-user.component.scss']
})
export class NewUserComponent implements OnInit, OnDestroy {
  public headerCol: IHeaderData;
  public newUserForm: FormGroup = new FormGroup({
    nombre: new FormControl(null, [Validators.required, Validators.maxLength(50)]),
    apellildos: new FormControl(null, [Validators.required, Validators.maxLength(50)]),
    dni: new FormControl(null, [Validators.required, this.utilValidatorsService.validateDNI]),
    email: new FormControl(null, [Validators.required, Validators.pattern(this.utilValidatorsService.emailPattern)]),
    codigoRol: new FormControl(null, [Validators.required]),
    search: new FormControl(null),
    codigoGestorCanal: new FormControl(null, Validators.required),
    soloLectura: new FormControl(false, Validators.required),
    accesoLiquidaciones: new FormControl(false, Validators.required),
    responsable: new FormControl(false, Validators.required),
  });
  public isLoading = false;
  public isLoadingResults = false;
  public permissions = PERMISSION;
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  public rolSelectedLabel = '';
  public rolSelectedPlaceholder = '';
  public managerList: IUserData[] = [];
  public hasSettlementAccess: boolean;
  public roleCode: string;
  @Output() formValid: EventEmitter<SetNewUser> = new EventEmitter();

  constructor(
    private userManagementService: UserManagementService,
    private authService: AuthService,
    private utilService: UtilService,
    private utilValidatorsService: UtilValidatorsService,
  ) { }

  ngOnInit(): void {
    this.changeUserRol();
    this.hasSettlementAccess = this.authService.getUserData().hasAccessToSettlement;
    this.roleCode = this.authService.getRole().code;
    this.newUserForm.statusChanges.subscribe(res => this.checkForm(res));
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public checkForm(res): void {
    const params = this.newUserForm.value;
    delete params.search;
    this.formValid.emit({
      valid: res === 'VALID',
      params
    });
  }

  public resetManagers(): void {
    this.managerList = [];
    this.newUserForm.controls.codigoGestorCanal.reset();
  }

  public search(rol: string, filter: string): void {
    this.isLoadingResults = true;
    let userRole = '';
    switch (rol) {
      case '300000003':
        userRole = USER_ROLE[USER_ROLE.gestor];
        break;
      case '300000004':
        userRole = USER_ROLE[USER_ROLE.comercial];
        break;
      case '300000005':
        userRole = USER_ROLE[USER_ROLE.backoffice];
        break;
    }
    this.userManagementService.getUserManagers({ filter }, userRole).pipe(takeUntil(this.destroyed$)).subscribe(res => {
      if (res.success) {
        this.managerList = res.data;
      } else {
        this.resetManagers();
        this.utilService.toastErrors(res);
      }
      this.isLoadingResults = false;
    }, error => {
      this.resetManagers();
      this.utilService.toastErrors(error);
      this.isLoadingResults = false;
    });
  }

  public handlePermission(event, permissionType: string): void {
    switch (permissionType) {
      case this.permissions[this.permissions['only read']]:
        this.newUserForm.get('soloLectura').setValue(event.checked);
        break;
      case this.permissions[this.permissions['settlement view']]:
        this.newUserForm.get('accesoLiquidaciones').setValue(event.checked);
        break;
      case this.permissions[this.permissions['create user']]:
        this.newUserForm.get('responsable').setValue(event.checked);
        break;
    }
  }

  private changeUserRol(): void {
    this.newUserForm.controls.codigoRol.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.search(value, '');
      this.newUserForm.controls.search.setValue('');
      this.newUserForm.controls.codigoGestorCanal.setValue(null);
      this.newUserForm.updateValueAndValidity();
      if (value === '300000004') {
        this.rolSelectedLabel = 'USER_MANAGEMENT.ASSOCIATE_MANAGER_TO_COMMERCIAL';
        this.rolSelectedPlaceholder = 'USER_MANAGEMENT.SEARCH_COMMERCIALS';
      }
      if (value === '300000003') {
        this.rolSelectedLabel = 'USER_MANAGEMENT.ASSOCIATE_MANAGER_TO_GESTOR';
        this.rolSelectedPlaceholder = 'USER_MANAGEMENT.SEARCH_MANAGER';
      }
      if (value === '300000005') {
        this.rolSelectedLabel = 'USER_MANAGEMENT.ASSOCIATE_MANAGER_TO_BACKOFFICE';
        this.rolSelectedPlaceholder = 'USER_MANAGEMENT.SEARCH_BACKOFFICE';
      }
    });
  }
}
